/**
 * Apple Pay Service (Native, framework-agnostic)
 *
 * Tokenizes a native Apple Pay sheet via BasisTheory and returns a
 * card-shaped token. No React, no checkout session, no Tagada client —
 * pure browser primitive suitable for Server-to-Server (S2S) partner
 * integrations.
 *
 * Use this when `paymentSetupConfig.apple_pay.provider === 'apple_pay'`
 * (i.e. native Apple Pay routed through Tagada/BasisTheory, not Stripe).
 * For Apple Pay routed through Stripe (`provider === 'stripe'`), use
 * `startStripeExpressCheckout` instead.
 *
 * Mirrors `@tagadapay/plugin-sdk/v2/standalone` apple-pay-service.ts so
 * partner code can rely on a single canonical home (`@tagadapay/core-js`)
 * without pulling the React-flavoured plugin-sdk.
 *
 * Must be invoked from a user gesture (click handler).
 *
 * @example
 * ```ts
 * import { isApplePayAvailable, startApplePaySession } from '@tagadapay/core-js';
 *
 * if (await isApplePayAvailable()) {
 *   button.addEventListener('click', () => {
 *     startApplePaySession(
 *       { basisTheoryApiKey: 'pk_***', countryCode: 'US', storeName: 'Acme' },
 *       { currency: 'USD', totalAmountMinor: 1999 },
 *       {
 *         onSuccess: async (token, contacts) => {
 *           // POST token.id + contacts to your server, then to
 *           // POST /api/public/v1/payment-instruments/create-from-token
 *         },
 *         onError: (msg) => console.error(msg),
 *       },
 *     );
 *   });
 * }
 * ```
 */

const APPLE_PAY_SDK_URL = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
const APPLE_PAY_SESSION_VERSION = 3;

let sdkLoadPromise: Promise<void> | null = null;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ApplePayServiceConfig {
  /** BasisTheory public API key */
  basisTheoryApiKey: string;
  /** Country code for the merchant (e.g. 'US') */
  countryCode?: string;
  /** Store / merchant display name shown in the Apple Pay sheet */
  storeName?: string;
}

export interface ApplePayPaymentRequest {
  /** ISO 4217 currency code (e.g. 'USD') */
  currency: string;
  /** Total amount in **minor units** (cents) */
  totalAmountMinor: number;
  /** Optional line items for the Apple Pay sheet */
  lineItems?: Array<{ label: string; amount: string }>;
  /** Optional shipping methods */
  shippingMethods?: Array<{
    identifier: string;
    label: string;
    detail?: string;
    amount: string;
  }>;
}

export interface ApplePayTokenResult {
  /** BasisTheory token ID — pass to /payment-instruments/create-from-token */
  id: string;
  type: string;
  card: {
    bin: string;
    last4: string;
    expiration_month: number;
    expiration_year: number;
    brand: string;
  };
}

export interface ApplePayShippingContact {
  phoneNumber?: string;
  emailAddress?: string;
  givenName?: string;
  familyName?: string;
  addressLines?: string[];
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
}

export interface ApplePayCallbacks {
  onSuccess: (token: ApplePayTokenResult, contacts: {
    shipping?: ApplePayShippingContact;
    billing?: ApplePayShippingContact;
  }) => void | Promise<void>;
  onError?: (error: string) => void;
  onCancel?: () => void;
  /** Called when the user changes their shipping contact in the sheet */
  onShippingContactSelected?: (contact: ApplePayShippingContact) => Promise<{
    total: { label: string; amount: string };
    lineItems: Array<{ label: string; amount: string }>;
    shippingMethods?: Array<{ identifier: string; label: string; detail?: string; amount: string }>;
  } | null>;
  /** Called when the user changes their shipping method in the sheet */
  onShippingMethodSelected?: (methodIdentifier: string) => Promise<{
    total: { label: string; amount: string };
    lineItems: Array<{ label: string; amount: string }>;
  } | null>;
}

// ---------------------------------------------------------------------------
// SDK Loading
// ---------------------------------------------------------------------------

function loadApplePaySdk(): Promise<void> {
  if (sdkLoadPromise) return sdkLoadPromise;

  if (typeof window === 'undefined') {
    return Promise.reject(new Error('[ApplePayService] No window — not in browser'));
  }

  if ((window as any).ApplePaySession) {
    sdkLoadPromise = Promise.resolve();
    return sdkLoadPromise;
  }

  const existing = document.querySelector(`script[src="${APPLE_PAY_SDK_URL}"]`);
  if (existing) {
    sdkLoadPromise = new Promise<void>((resolve) => {
      if ((window as any).ApplePaySession) return resolve();
      existing.addEventListener('load', () => resolve());
      const check = setInterval(() => {
        if ((window as any).ApplePaySession) { clearInterval(check); resolve(); }
      }, 100);
      setTimeout(() => { clearInterval(check); resolve(); }, 5000);
    });
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APPLE_PAY_SDK_URL;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkLoadPromise = null;
      reject(new Error('[ApplePayService] Failed to load Apple Pay SDK'));
    };
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

// ---------------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------------

/**
 * Loads the Apple Pay SDK and probes device capability. Returns `true`
 * on Safari/iOS with Apple Pay configured, `false` otherwise. Safe to
 * call repeatedly — the SDK is cached.
 */
export async function isApplePayAvailable(): Promise<boolean> {
  try {
    await loadApplePaySdk();
    const ApplePaySession = (window as any).ApplePaySession;
    return !!ApplePaySession && ApplePaySession.canMakePayments();
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// BasisTheory API Helpers
// ---------------------------------------------------------------------------

async function validateMerchant(
  basisTheoryApiKey: string,
  displayName: string,
  domain: string,
): Promise<any> {
  const response = await fetch('https://api.basistheory.com/apple-pay/session', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'BT-API-KEY': basisTheoryApiKey,
    },
    body: JSON.stringify({
      display_name: displayName,
      domain,
    }),
  });

  if (!response.ok) {
    throw new Error(`[ApplePayService] Merchant validation failed: HTTP ${response.status}`);
  }

  return response.json();
}

async function tokenizeApplePayPayment(
  basisTheoryApiKey: string,
  paymentToken: any,
): Promise<ApplePayTokenResult> {
  const response = await fetch('https://api.basistheory.com/apple-pay', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'BT-API-KEY': basisTheoryApiKey,
    },
    body: JSON.stringify({
      apple_payment_data: paymentToken,
    }),
  });

  if (!response.ok) {
    throw new Error(`[ApplePayService] Tokenization failed: HTTP ${response.status}`);
  }

  const result = await response.json();
  return result.apple_pay;
}

// ---------------------------------------------------------------------------
// Currency Helpers
// ---------------------------------------------------------------------------

function minorToDecimal(amountMinor: number, currency: string): string {
  const zeroDecimal = ['BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
    'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'];
  if (zeroDecimal.includes(currency.toUpperCase())) {
    return String(amountMinor);
  }
  return (amountMinor / 100).toFixed(2);
}

// ---------------------------------------------------------------------------
// Session Creation
// ---------------------------------------------------------------------------

/**
 * Opens the native Apple Pay sheet, drives merchant validation +
 * tokenization through BasisTheory, and surfaces the resulting token
 * via `callbacks.onSuccess`. Must be called from a user gesture.
 */
export function startApplePaySession(
  config: ApplePayServiceConfig,
  request: ApplePayPaymentRequest,
  callbacks: ApplePayCallbacks,
): void {
  const ApplePaySession = (window as any).ApplePaySession;
  if (!ApplePaySession) {
    callbacks.onError?.('Apple Pay is not available on this device');
    return;
  }

  const countryCode = config.countryCode || 'US';
  const storeName = config.storeName || 'Store';
  const totalStr = minorToDecimal(request.totalAmountMinor, request.currency);

  const applePayRequest: any = {
    countryCode,
    currencyCode: request.currency,
    supportedNetworks: ['visa', 'masterCard', 'amex', 'discover'],
    merchantCapabilities: ['supports3DS'],
    total: {
      label: storeName,
      amount: totalStr,
      type: 'final',
    },
    requiredShippingContactFields: ['name', 'phone', 'email', 'postalAddress'],
    requiredBillingContactFields: ['postalAddress'],
  };

  if (request.lineItems) {
    applePayRequest.lineItems = request.lineItems.map(li => ({ ...li, type: 'final' }));
  }
  if (request.shippingMethods) {
    applePayRequest.shippingMethods = request.shippingMethods;
  }

  try {
    const session = new ApplePaySession(APPLE_PAY_SESSION_VERSION, applePayRequest);

    session.onvalidatemerchant = () => {
      void (async () => {
        try {
          const merchantSession = await validateMerchant(
            config.basisTheoryApiKey,
            storeName,
            window.location.host,
          );
          session.completeMerchantValidation(merchantSession);
        } catch (err) {
          console.error('[ApplePayService] Merchant validation failed:', err);
          session.abort();
          callbacks.onError?.('Merchant validation failed');
        }
      })();
    };

    session.onpaymentauthorized = (event: any) => {
      void (async () => {
        try {
          const token = await tokenizeApplePayPayment(
            config.basisTheoryApiKey,
            event.payment.token,
          );

          session.completePayment(ApplePaySession.STATUS_SUCCESS);

          await callbacks.onSuccess(token, {
            shipping: event.payment.shippingContact,
            billing: event.payment.billingContact,
          });
        } catch (err) {
          console.error('[ApplePayService] Payment processing failed:', err);
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          callbacks.onError?.('Payment failed');
        }
      })();
    };

    if (callbacks.onShippingContactSelected) {
      session.onshippingcontactselected = (event: any) => {
        void (async () => {
          try {
            const result = await callbacks.onShippingContactSelected!(event.shippingContact);
            if (!result) {
              session.abort();
              return;
            }
            session.completeShippingContactSelection(
              ApplePaySession.STATUS_SUCCESS,
              result.shippingMethods || [],
              { ...result.total, type: 'final' },
              result.lineItems.map(li => ({ ...li, type: 'final' })),
            );
          } catch {
            session.abort();
          }
        })();
      };
    }

    if (callbacks.onShippingMethodSelected) {
      session.onshippingmethodselected = (event: any) => {
        void (async () => {
          try {
            const result = await callbacks.onShippingMethodSelected!(
              event.shippingMethod.identifier,
            );
            if (!result) {
              session.abort();
              return;
            }
            session.completeShippingMethodSelection(
              ApplePaySession.STATUS_SUCCESS,
              { ...result.total, type: 'final' },
              result.lineItems.map(li => ({ ...li, type: 'final' })),
            );
          } catch {
            session.abort();
          }
        })();
      };
    }

    session.oncancel = () => {
      callbacks.onCancel?.();
    };

    session.onerror = (event: any) => {
      console.error('[ApplePayService] Session error:', event);
      callbacks.onError?.('Apple Pay session error');
    };

    session.begin();
  } catch (err) {
    console.error('[ApplePayService] Failed to start Apple Pay session:', err);
    callbacks.onError?.('Failed to start Apple Pay');
  }
}
